<template>
  <!-- Loading: skeleton shimmer row -->
  <section v-if="loading" class="deal-shelf">
    <h3 class="section-title">
      <span class="section-icon">🔥</span>
      Biggest Price Drops
    </h3>
    <div class="shelf-row">
      <div v-for="n in 3" :key="`skel-${n}`" class="skeleton-card" aria-hidden="true">
        <div class="skeleton-strip shimmer"></div>
        <div class="skeleton-body">
          <div class="skeleton-line shimmer skeleton-line--title"></div>
          <div class="skeleton-line shimmer skeleton-line--pill"></div>
          <div class="skeleton-line shimmer skeleton-line--meta"></div>
          <div class="skeleton-panel shimmer"></div>
          <div class="skeleton-btn shimmer"></div>
        </div>
      </div>
    </div>
  </section>

  <!-- Error: inline retry -->
  <section v-else-if="error" class="deal-shelf">
    <h3 class="section-title">
      <span class="section-icon">🔥</span>
      Biggest Price Drops
    </h3>
    <div class="shelf-error">
      <p>{{ error }}</p>
      <button type="button" class="app-retry-btn" @click="fetchDeals">Try Again</button>
    </div>
  </section>

  <!-- Loaded. Cards are real drops only. Empty is an honest empty, not filler. -->
  <section v-else class="deal-shelf">
    <div class="shelf-header-group">
      <div class="shelf-title-row">
        <h3 class="section-title">
          <span class="section-icon">🔥</span>
          Biggest Price Drops
        </h3>
        <deal-explainer-popover
          title="How We Calculate Price Drops"
          :definitions="priceDropDefinitions"
          aria-label="Deal calculation explanation"
        />
      </div>
      <p class="shelf-trust-note">
        <i class="fas fa-shield-alt trust-icon" aria-hidden="true"></i>
        Fares tracked daily from cruise line sources. Savings compare current fares against our 30-day recorded peak for each cabin and sail date, not cruise line list prices.
      </p>
    </div>
    <div v-if="hasDeals" class="shelf-row">
      <div v-for="(deal, index) in deals" :key="`hot-${deal.id}`" class="deal-card">
        <div class="deal-rank">{{ index + 1 }}</div>
        <CardIllustrationStrip
          :destination="deal.destination"
          :destination-id="deal.destination_id"
          :name="deal.name"
        />

        <div class="deal-body">
          <h4 class="cs-title-case deal-title">{{ titleCase(deal.name) }}</h4>
          <p class="deal-subline">
            <span v-if="deal.ship_name">{{ deal.ship_name }}</span>
            <span v-if="deal.ship_name && deal.cruise_line" aria-hidden="true"> · </span>
            <span v-if="deal.cruise_line" class="cs-line-pill">{{ deal.cruise_line }}</span>
          </p>
          <p class="deal-meta">
            <template v-if="deal.departure_port">From {{ deal.departure_port }}</template>
            <template v-if="deal.departure_port && deal.duration"> · </template>
            <template v-if="deal.duration">{{ nightsLabel(deal.duration) }}</template>
            <template v-if="deal.departure_port || deal.duration"> · </template>
            {{ formatDate(deal.sail_date || deal.departure_date) }}
          </p>
        </div>

        <div class="deal-pricing">
          <template v-if="hasDealPrice(deal)">
            <div v-if="deal.peak_price" class="old-price">${{ formatPrice(deal.peak_price) }} <span v-if="deal.peak_cabin_type" class="cabin-type">({{ deal.peak_cabin_type }})</span></div>
            <div class="current-price">${{ formatPrice(deal.current_price) }} <span v-if="deal.current_cabin_type" class="cabin-type">({{ deal.current_cabin_type }})</span></div>
            <div class="price-freshness">{{ priceCue(deal) }}</div>
            <div v-if="deal.price_drop" class="savings">
              Save ${{ formatPrice(deal.price_drop) }} ({{ Math.round(deal.price_drop_percentage) }}%)
            </div>
          </template>
          <template v-else>
            <div class="current-price price-unavailable">{{ hiddenPriceReason(deal) }}</div>
            <div v-if="staleAgeStamp(deal)" class="price-freshness">{{ staleAgeStamp(deal) }}</div>
          </template>
        </div>

        <div class="deal-actions">
          <router-link :to="getCruiseUrl(deal.id, deal.sail_date)" class="view-deal-btn" :aria-label="sailingLinkAriaLabel(deal)">{{ sailingLinkText(deal) }}</router-link>
          <a v-affiliate-link
            v-if="canBook(deal.cruise_line)"
            :href="affiliateUrl(deal.id)"
            target="_blank"
            rel="noopener noreferrer sponsored"
            class="cs-btn-book-outline deal-book-btn"
            :aria-label="ctaAriaLabel(deal)"
          >
            {{ tierCta(deal) }} <i class="fas fa-external-link-alt" aria-hidden="true"></i>
          </a>
          <span v-else class="cs-btn-book-disabled deal-book-quiet" role="status" title="Direct booking is not available on this site">Not Bookable Here</span>
        </div>
        <p v-if="canBook(deal.cruise_line)" class="deal-affiliate-note">{{ tierDealNote(deal) }}</p>
      </div>
    </div>
    <p v-else class="shelf-empty">No sailings are priced below a recorded 30-day peak right now.</p>
  </section>
</template>

<script>
import apiService from '@/services/api';
import { canBookLine } from '@/utils/affiliateFlag';
import { dealNote, tierCtaLabel } from '@/utils/bookingTierCopy';
import { actionAriaLabel, sailingLinkAriaLabel, sailingLinkText } from '@/utils/sailingLinkLabel';
import DealExplainerPopover from '@/components/DealExplainerPopover.vue';
import { hiddenPriceReason, listingPriceCue, showsListingPrice, staleAgeStamp } from '@/utils/priceFreshness';
import { realPriceDropDeals } from '@/utils/priceDropBadge';

import CardIllustrationStrip from '@/components/CardIllustrationStrip.vue'

import { formatPriceAmount } from '@/utils/formatMoney'
import { formatPlaceTitle } from '@/utils/formatPlaceTitle'
export default {
  name: 'HotDealShelf',
  props: {
    affiliateEnabled: {
      type: Boolean,
      default: false
    }
  },
  components: {
    DealExplainerPopover,
    CardIllustrationStrip
  },
  data() {
    return {
      deals: [],
      loading: true,
      error: null
    };
  },
  computed: {
    hasDeals() {
      return !this.loading && !this.error && this.deals.length > 0;
    },
    priceDropDefinitions() {
      return [
        {
          label: 'Price Drop',
          body: 'We monitor daily fares directly from cruise line listings. When a cabin on a specific sail date drops below its 30-day recorded peak, we flag it as a Price Drop with the dollar and percentage savings.'
        }
      ];
    }
  },
  mounted() {
    this.fetchDeals();
  },
  methods: {
    // CP-212: one pattern for every sailing link (visible text + ship/date accessible name).
    sailingLinkText,
    sailingLinkAriaLabel,
    // CP-212: the CruiseDirect link's accessible name leads with its visible text, then the sailing.
    ctaAriaLabel(deal) {
      return actionAriaLabel(this.tierCta(deal), deal, '(opens in a new tab)');
    },
    // CP-143: ON-state copy from the landing tier (only reached when canBook is true).
    tierCta(deal) {
      return tierCtaLabel(deal && deal.affiliate_landing_tier);
    },
    tierDealNote(deal) {
      return dealNote({
        tier: deal && deal.affiliate_landing_tier,
        line: deal && deal.cruise_line,
        collectedAt: deal && deal.price_updated_at,
      });
    },
    // CP-141: CTA only when the flag is on and CruiseDirect sells the line.
    canBook(line) {
      return canBookLine(this.affiliateEnabled, line);
    },
    async fetchDeals() {
      this.loading = true;
      this.error = null;

      try {
        const response = await apiService.getHotDeals({
          limit: 10,
          include_price_drops: true,
          include_best_value: false,
          include_newly_listed: false
        });
        this.deals = realPriceDropDeals(response.data.price_drops);
      } catch (error) {
        console.error('Error fetching hot deals:', error);
        this.error = 'Unable to load hot deals.';
      } finally {
        this.loading = false;
      }
    },
    formatDate(dateString) {
      if (!dateString) return 'TBD';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },
    formatPrice(price) {
      return formatPriceAmount(price);
    },
    hasDealPrice(deal) {
      return showsListingPrice(deal);
    },
    priceCue(deal) {
      return listingPriceCue(deal);
    },
    staleAgeStamp,
    hiddenPriceReason,
    getCruiseUrl(cruiseId, sailDate) {
      let url = `/cruise/${cruiseId}`;
      if (sailDate) {
        url += `?sail_date=${encodeURIComponent(sailDate)}`;
      }
      return url;
    },
    affiliateUrl(cruiseId) {
      return `/api/affiliate/redirect?cruise_id=${cruiseId}&partner=cruisedirect&source=home_hot`;
    },
    titleCase(str) {
      return formatPlaceTitle(str);
    },
    nightsLabel(n) {
      const num = Number(n);
      if (!Number.isFinite(num) || num <= 0) return '';
      const rounded = Math.round(num);
      return rounded === 1 ? '1 night' : `${rounded} nights`;
    },
  }
};
</script>

<style scoped>
.deal-shelf {
  padding: var(--space-6) 0 var(--space-5);
}

.shelf-header-group {
  margin-bottom: var(--space-4);
}

.shelf-title-row {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  margin-bottom: var(--space-1);
}

.shelf-title-row .section-title {
  flex: 1;
  margin: 0;
}

.shelf-trust-note {
  margin: var(--space-2) 0 0;
  display: flex;
  align-items: baseline;
  gap: var(--space-2);
  font-family: var(--font-body);
  font-size: var(--text-caption);
  color: var(--cs-muted);
  line-height: var(--line-height-body);
}

.trust-icon {
  color: var(--cs-signal-teal);
}

.section-title {
  color: var(--cs-ink);
  margin: 0 0 var(--space-4);
  font-size: 1.25rem;
  font-family: var(--font-heading);
  border-bottom: 2px solid var(--cs-card-border);
  padding-bottom: var(--space-2);
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.section-icon { font-size: 1.4rem; }

/* Horizontal scroll-snap row — on every viewport, never a grid */
.shelf-row {
  display: flex;
  gap: var(--space-4);
  overflow-x: auto;
  overflow-y: hidden;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  padding-bottom: var(--space-2);
  /* CP-97: kill chunky dated horizontal scrollbar chrome; scroll still works */
  scrollbar-width: none;
  -ms-overflow-style: none;
}
.shelf-row::-webkit-scrollbar {
  display: none;
  width: 0;
  height: 0;
}

.deal-card,
.skeleton-card {
  scroll-snap-align: start;
  flex-shrink: 0;
  width: 280px;
  min-height: 380px;
  border-radius: var(--radius-lg);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background: var(--cs-card);
  border: 1px solid var(--cs-card-border);
  box-shadow: var(--shadow-card);
}

.deal-card {
  position: relative;
  transition: transform var(--transition-normal), box-shadow var(--transition-normal);
}

.deal-card:hover {
  transform: translateY(-3px);
  box-shadow: var(--shadow-card-hover);
}

.deal-rank {
  position: absolute;
  top: var(--space-3);
  left: var(--space-3);
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--cs-fill);
  color: white;
  border-radius: 50%;
  font-weight: bold;
  font-size: var(--text-body);
  z-index: 1;
}


.deal-body {
  padding: var(--space-4);
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  flex: 1;
}

.deal-title {
  margin: 0;
}

.deal-subline {
  margin: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2);
  font-size: 13px;
  line-height: 18px;
  color: var(--cs-muted);
}

.deal-meta {
  margin: 0;
  font-size: var(--text-small);
  color: var(--cs-muted);
  line-height: 1.45;
}

.deal-pricing {
  margin: 0 var(--space-4) var(--space-4);
  padding: var(--space-3);
  background-color: var(--color-accent-success-bg);
  border-radius: var(--radius-md);
}

.old-price {
  text-decoration: line-through;
  color: var(--cs-muted);
  font-size: var(--text-small);
}

.current-price {
  font-size: 1.35rem;
  font-weight: 700;
  color: var(--cs-price);
  margin: 0.25rem 0;
}

.savings {
  color: var(--cs-savings-text);
  font-weight: 700;
  font-size: var(--text-small);
}

.cabin-type {
  font-size: 0.75rem;
  color: var(--cs-muted);
  font-weight: normal;
  font-style: italic;
}

.view-deal-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  /* CP-212: labels are now "See N-night <destination> sailing" and can wrap to two lines in a narrow card.
     Every button reserves the two-line height (2 x 22px + 2 x 12px padding) so cards in a row stay aligned
     and the skeleton matches the loaded card. */
  min-height: var(--card-link-min-height);
  text-wrap: balance;
  padding: var(--space-3);
  background-color: var(--cs-fill);
  color: white;
  text-align: center;
  border-radius: var(--radius-md);
  text-decoration: none;
  font-weight: 700;
  transition: background-color var(--transition-normal);
  box-sizing: border-box;
}

.view-deal-btn:hover {
  background-color: var(--cs-fill-hover);
}

.deal-actions {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: var(--space-2);
  margin: auto var(--space-4) var(--space-3);
}

.deal-book-btn {
  width: 100%;
  box-sizing: border-box;
}

.deal-book-quiet {
  align-self: center;
  margin: 0 0 var(--space-1);
}

.deal-affiliate-note {
  margin: var(--space-2) var(--space-4) var(--space-4);
  font-size: 11px;
  color: var(--cs-muted);
  line-height: 1.4;
}



.shelf-error {
  text-align: center;
  padding: var(--space-5);
  color: var(--color-error);
  background: var(--color-error-bg);
  border-radius: var(--radius-md);
}

.shelf-error p {
  margin: 0;
}

.shelf-empty {
  margin: 0;
  padding: var(--space-4);
  color: var(--cs-muted, var(--color-text-muted));
  background: var(--cs-signal-mist, var(--cs-sand));
  border-radius: var(--radius-md);
}

/* Skeleton shimmer (deliberately not the app spinner ring) */
.skeleton-card {
  pointer-events: none;
}

.skeleton-strip {
  height: 88px;
  width: 100%;
  flex-shrink: 0;
}

.skeleton-body {
  padding: var(--space-4);
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  flex: 1;
}

.skeleton-line {
  border-radius: var(--radius-sm);
  height: 14px;
}

.skeleton-line--title { height: 24px; width: 85%; }
.skeleton-line--pill { height: 20px; width: 55%; border-radius: var(--radius-pill); }
.skeleton-line--meta { width: 70%; }

.skeleton-panel {
  height: 88px;
  border-radius: var(--radius-md);
  margin-top: auto;
}

.skeleton-btn {
  height: var(--card-link-min-height);
  border-radius: var(--radius-md);
}

.shimmer {
  background: linear-gradient(
    90deg,
    var(--color-border-light) 0%,
    var(--cs-signal-mist) 40%,
    var(--color-border-light) 80%
  );
  background-size: 300% 100%;
  animation: shelf-shimmer 1.4s ease-in-out infinite;
}

@keyframes shelf-shimmer {
  0% { background-position: 150% 0; }
  100% { background-position: -150% 0; }
}

@media (prefers-reduced-motion: reduce) {
  .shimmer {
    animation: none;
  }
}

@media (min-width: 768px) {
  .skeleton-strip {
    height: 96px;
  }
}

.price-freshness {
  margin-top: var(--space-2);
  font-size: var(--text-caption);
  line-height: 1.35;
  color: var(--color-text-muted);
}

.price-unavailable {
  color: var(--color-text-muted);
  font-style: italic;
  font-size: var(--text-small);
  font-weight: 600;
}
</style>
